import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import style from './style.module.css';
import ChapterBlock from '../../parts/ChapterBlock/ChapterBlock';
import Card from '../../components/Card/Card';

import { chapterLabel, chapterNum } from '../../utils/helpers';

import { 
  getUserChaptersData, getUserFirstName, getLanguageArray, 
  Language, getToken, urlLogin, 
  welcomeLabel, welcomeIntroLabel, 
  completedCoursesLabel,
  completedOutOfLabel,
  academyLabel,
  goToAcademyLabel,
  urlAcademy,
  achievementsLabel,
  urlActivity,
  viewYourAchievementsLabel,
  activityLabel,
  urlScore,
  latestActivityLabel,
  academyIntroTextDashboardLabel,
  simulationSummaryLabel,
  simulationSummaryIntroTextDashboardLabel,
  phishingAttemptsReportedLabel,
  phishingAttemptsFailedLabel,
  outOfLabel,
  systemLanguage,
  getLabel,
  getUserPhishingSummary
} from '../../utils/constants';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire, faHeartCircleBolt, faMessage, faSchool } from '@fortawesome/free-solid-svg-icons';

const DashboardView: React.FC = () => {
    const activeToken = getToken();
    const navigate = useNavigate();

    const [loading, isLoading] = useState(true);
    const [sessionLang, setSessionLang] = useState<Language>(getLanguageArray());

    const filteredChapters = (() => {
        const lastInProgressIndex = getUserChaptersData().map(ch => ch.status).lastIndexOf('in_progress');
        if (lastInProgressIndex === -1) return [];
        return getUserChaptersData()
            .slice(Math.max(0, lastInProgressIndex - 1), lastInProgressIndex + 1)
            .reverse();
    })();

    const { totalModules, completedModules } = getUserChaptersData().reduce(
        (acc, chapter) => {
            chapter.modules.forEach(module => {
                acc.totalModules++;
                if (module.status === "complete") acc.completedModules++;
            });
            return acc;
        }, { totalModules: 0, completedModules: 0 }
    );

    const chapterPercentage = totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;

    const phishingData = getUserPhishingSummary()
    const phishingSummary = {
        correct_attempts: phishingData.correct_attempts ?? 0,
        correct_percentage: phishingData.total_attempts > 0
            ? parseFloat(((phishingData.correct_attempts / phishingData.total_attempts) * 100).toFixed(1))
            : 0,
        failed_attempts: phishingData.failed_attempts ?? 0,
        failed_percentage: phishingData.total_attempts > 0
            ? parseFloat(((phishingData.failed_attempts / phishingData.total_attempts) * 100).toFixed(1))
            : 0,
        total_attempts: phishingData.total_attempts
    };


    useEffect(() => {
        if (!activeToken) {
            navigate(urlLogin);
            return;
        }
        isLoading(false);
    }, [activeToken]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === systemLanguage && e.newValue) {
                setSessionLang(JSON.parse(e.newValue));
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, []);

    const lang = sessionLang.short || 'EN';

    return (
        <div className={style.dashboard}>
            <div className={style.profile}>
                <div className={`${style.sectionTitle} ${style.textContainerGrad}`}>
                    {getLabel(welcomeLabel, sessionLang)}, {getUserFirstName()}
                </div>
                <div className={style.info}>
                    {getLabel(welcomeIntroLabel, sessionLang)}
                </div>
                <div className={style.quickLinks}>
                    <div className={style.four}>
                        <Card
                            type="usage"
                            usageData={{
                                title: getLabel(completedCoursesLabel, sessionLang),
                                title2: "",
                                object: completedModules.toString(),
                                details: `${completedModules} ${getLabel(completedOutOfLabel, sessionLang)} ${totalModules} (${chapterPercentage}%)`,
                                details2: "",
                                percentage: chapterPercentage
                            }}
                        />
                    </div>
                    <div className={style.four}>
                        <Card
                            type="icon"
                            iconData={{
                                icon: <FontAwesomeIcon icon={faSchool} />,
                                color: "blue",
                                title: getLabel(academyLabel, sessionLang),
                                link: getLabel(goToAcademyLabel, sessionLang),
                                href: urlAcademy
                            }}
                        />
                    </div>
                    <div className={style.four}>
                        <Card
                            type="icon"
                            iconData={{
                                icon: <FontAwesomeIcon icon={faFire} />,
                                color: "red",
                                title: getLabel(achievementsLabel, sessionLang),
                                link: getLabel(viewYourAchievementsLabel, sessionLang),
                                href: urlScore
                            }}
                        />
                    </div>
                    <div className={style.four}>
                        <Card
                            type="icon"
                            iconData={{
                                icon: <FontAwesomeIcon icon={faHeartCircleBolt} />,
                                color: "purple",
                                title: getLabel(activityLabel, sessionLang),
                                link: getLabel(latestActivityLabel, sessionLang),
                                href: urlActivity
                            }}
                        />
                    </div>
                </div>

                <div className={style.header}>
                    <div className={`${style.greeting} ${style.textContainerGrad}`}>
                        {getLabel(academyLabel, sessionLang)}
                    </div>
                    <div className={`${style.info} ${style.noSpace}`}>
                        {getLabel(academyIntroTextDashboardLabel, sessionLang)}
                    </div>
                </div>

                <div className={style.chapters}>
                    {filteredChapters.map(item => {
                        const moduleProgress = item.modules
                            .filter(module => module.status === 'complete' || module.status === 'in_progress')
                            .map(module => ({
                                status: module.status as 'complete' | 'in_progress',
                                label: module.label[lang],
                                link: module.link
                            }));

                        return (
                            <ChapterBlock
                                key={item.label}
                                num={chapterNum(item.label)}
                                summary={true}
                                content={chapterLabel(item.chapter, sessionLang)}
                                statusIcon={item.status}
                                progress={moduleProgress}
                                totalNumber={item.modules.length}
                            />
                        );
                    })}
                </div>

                <div className={style.header}>
                    <div className={`${style.greeting} ${style.textContainerGrad}`}>
                        {getLabel(simulationSummaryLabel, sessionLang)}
                    </div>
                    <div className={`${style.info} ${style.noSpace}`}>
                        {getLabel(simulationSummaryIntroTextDashboardLabel, sessionLang)}
                    </div>
                </div>

                <div className={`${style.quickLinks} ${style.summary}`}>
                    <div className={style.two}>
                        <Card
                            type="usage"
                            usageData={{
                                title: getLabel(phishingAttemptsReportedLabel, sessionLang),
                                title2: "",
                                object: (phishingSummary.correct_attempts ?? 0).toString(),
                                details: `${(
                                    phishingSummary.correct_attempts ?? 0).toString()}
                                    ${getLabel(outOfLabel, sessionLang)} ${(phishingSummary.total_attempts ?? 0).toString()} 
                                    (${(phishingSummary.correct_percentage ?? 0).toString()}%)`,
                                details2: "",
                                percentage: phishingSummary.correct_percentage,
                                color:"green"
                            }}
                        />
                    </div>
                    <div className={style.two}>
                        <Card
                            type="usage"
                            usageData={{
                                title: getLabel(phishingAttemptsFailedLabel, sessionLang),
                                title2: "",
                                object: (phishingSummary.failed_attempts ?? 0).toString(),
                                details: `${(
                                    phishingSummary.failed_attempts ?? 0).toString()}
                                    ${getLabel(outOfLabel, sessionLang)} ${(phishingSummary.total_attempts ?? 0).toString()} 
                                    (${(phishingSummary.failed_percentage ?? 0).toString()}%)`,
                                details2: "",
                                percentage: phishingSummary.failed_percentage,
                                color:"red"
                            }}
                            chart="red"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardView


